import { type BuildingId, getBuildingById } from './BuildingData';
import type { Labyrinth} from '../labyrinth/Labyrinth';
import { Tile } from '../labyrinth/Labyrinth';

export interface PlacedBuilding {
  id: BuildingId;
  level: number;      // 1-based (1 = just built)
  gridX: number;      // tile X within base-local grid (0-based)
  gridY: number;      // tile Y within base-local grid (0-based)
}

export class BuildingSystem {
  private _buildings: PlacedBuilding[] = [];
  /** Fired whenever the placed-building set changes. The `placed` flag is
   *  true for a newly-placed building (used by the achievement system to
   *  increment the lifetime-placed counter) and false for upgrades/moves. */
  onChange: ((placed: boolean) => void) | null = null;

  // Base area bounds (set from labyrinth)
  private _baseOriginX = 0;
  private _baseOriginY = 0;
  private _baseW = 0;
  private _baseH = 0;
  private _baseMask: boolean[][] = []; // true = valid base tile (local coords)

  /** Reference to labyrinth for syncing building collision tiles */
  private _labyrinth: Labyrinth | null = null;

  /** Timestamp of last passive-credit tick */
  private _lastCreditTick: number = Date.now();

  get buildings(): readonly PlacedBuilding[] { return this._buildings; }
  get baseOriginX() { return this._baseOriginX; }
  get baseOriginY() { return this._baseOriginY; }
  get baseW() { return this._baseW; }
  get baseH() { return this._baseH; }
  get baseMask() { return this._baseMask; }

  // ─── Base bounds ────────────────────────────────────────

  /** Scan the labyrinth to determine the bounding box of base tiles */
  initBaseBounds(labyrinth: Labyrinth): void {
    this._labyrinth = labyrinth;
    let minX = labyrinth.width, minY = labyrinth.height;
    let maxX = -1, maxY = -1;

    for (let y = 0; y < labyrinth.height; y++) {
      for (let x = 0; x < labyrinth.width; x++) {
        if (labyrinth.tiles[y]![x] === Tile.Base) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < 0) {
      // No base tiles found — fallback
      this._baseOriginX = labyrinth.baseTile.x - 17;
      this._baseOriginY = labyrinth.baseTile.y - 17;
      this._baseW = 35;
      this._baseH = 35;
      this._baseMask = Array.from({ length: 35 }, () => new Array<boolean>(35).fill(true));
      this.syncBuildingCollision();
      return;
    }

    this._baseOriginX = minX;
    this._baseOriginY = minY;
    this._baseW = maxX - minX + 1;
    this._baseH = maxY - minY + 1;

    // Build mask of valid base tiles in local coords
    this._baseMask = Array.from({ length: this._baseH }, (_, ly) =>
      Array.from({ length: this._baseW }, (_, lx) =>
        labyrinth.tiles[minY + ly]![minX + lx] === Tile.Base
      )
    );
    this.syncBuildingCollision();
  }

  /** Push current building footprints into the labyrinth's blocked-tile set. */
  syncBuildingCollision(): void {
    if (!this._labyrinth) return;
    const blocked = new Set<string>();
    for (const b of this._buildings) {
      const def = getBuildingById(b.id);
      if (!def) continue;
      for (let dy = 0; dy < def.footprintH; dy++) {
        for (let dx = 0; dx < def.footprintW; dx++) {
          const wx = this._baseOriginX + b.gridX + dx;
          const wy = this._baseOriginY + b.gridY + dy;
          blocked.add(`${wx},${wy}`);
        }
      }
    }
    this._labyrinth.setBuildingBlocked(blocked);
  }

  // ─── Placement ──────────────────────────────────────────

  /** Check if a building footprint fits at (gx, gy) in local grid coords */
  canPlace(id: BuildingId, gx: number, gy: number, excludeIndex?: number): boolean {
    const def = getBuildingById(id);
    if (!def) return false;

    // Check bounds and base mask
    for (let dy = 0; dy < def.footprintH; dy++) {
      for (let dx = 0; dx < def.footprintW; dx++) {
        const lx = gx + dx;
        const ly = gy + dy;
        if (lx < 0 || lx >= this._baseW || ly < 0 || ly >= this._baseH) return false;
        if (!this._baseMask[ly]?.[lx]) return false;
      }
    }

    // Check overlap with existing buildings
    for (let i = 0; i < this._buildings.length; i++) {
      if (i === excludeIndex) continue;
      const b = this._buildings[i]!;
      const bDef = getBuildingById(b.id);
      if (!bDef) continue;
      if (this.footprintsOverlap(
        gx, gy, def.footprintW, def.footprintH,
        b.gridX, b.gridY, bDef.footprintW, bDef.footprintH,
      )) return false;
    }

    return true;
  }

  /** Place a building. Returns true on success. */
  place(id: BuildingId, gx: number, gy: number): boolean {
    // Only one of each type allowed
    if (this._buildings.some(b => b.id === id)) return false;
    if (!this.canPlace(id, gx, gy)) return false;

    this._buildings.push({ id, level: 1, gridX: gx, gridY: gy });
    this.syncBuildingCollision();
    this.onChange?.(true);
    return true;
  }

  /** Remove a building by index */
  remove(index: number) {
    if (index >= 0 && index < this._buildings.length) {
      this._buildings.splice(index, 1);
      this.syncBuildingCollision();
      this.onChange?.(false);
    }
  }

  /** Move a building to a new position. Returns true on success. */
  move(index: number, newGx: number, newGy: number): boolean {
    const b = this._buildings[index];
    if (!b) return false;
    if (!this.canPlace(b.id, newGx, newGy, index)) return false;
    b.gridX = newGx;
    b.gridY = newGy;
    this.syncBuildingCollision();
    return true;
  }

  /** Upgrade a building. Returns the cost if successful, 0 if failed. */
  upgrade(index: number, availableMoney: number): number {
    const b = this._buildings[index];
    if (!b) return 0;
    const def = getBuildingById(b.id);
    if (!def || b.level >= def.maxLevel) return 0;
    const cost = def.costs[b.level]; // costs[0]=lv1 build, costs[1]=lv2 upgrade, etc.
    if (cost === undefined || availableMoney < cost) return 0;
    b.level++;
    this.onChange?.(false);
    return cost;
  }

  /** Get placed building level (0 = not built) */
  getBuildingLevel(id: BuildingId): number {
    const b = this._buildings.find(b => b.id === id);
    return b ? b.level : 0;
  }

  /** Find placed building index by id */
  findBuilding(id: BuildingId): number {
    return this._buildings.findIndex(b => b.id === id);
  }

  // ─── Effect queries ─────────────────────────────────────

  /** Sell price bonus (0.0 to 0.30) */
  getSellPriceBonus(): number {
    const lv = this.getBuildingLevel('scrap_yard');
    return [0, 0.10, 0.20, 0.30][lv] ?? 0;
  }

  /** Credit multiplier for kills (1.0 to 1.50) */
  getCreditMultiplier(): number {
    const lv = this.getBuildingLevel('signal_beacon');
    return [1.0, 1.15, 1.30, 1.50][lv] ?? 1.0;
  }

  /** Weapon damage multiplier (1.0 to 1.15) */
  getDamageMultiplier(): number {
    const lv = this.getBuildingLevel('armory');
    return [1.0, 1.05, 1.10, 1.15][lv] ?? 1.0;
  }

  /** Damage reduction bonus (0.0 to 0.10) */
  getDamageReductionBonus(): number {
    const lv = this.getBuildingLevel('shield_pylon');
    return [0, 0.03, 0.06, 0.10][lv] ?? 0;
  }

  /** Detection radius bonus */
  getDetectionBonus(): number {
    const lv = this.getBuildingLevel('radar_tower');
    return [0, 1, 2, 3][lv] ?? 0;
  }

  /** Vision radius bonus */
  getVisionBonus(): number {
    const lv = this.getBuildingLevel('radar_tower');
    return [0, 0.5, 1.0, 1.5][lv] ?? 0;
  }

  /** Quality bump chance (0.0 to 0.15) */
  getQualityBonus(): number {
    const lv = this.getBuildingLevel('loot_enhancer');
    return [0, 0.05, 0.10, 0.15][lv] ?? 0;
  }

  /** Passive credits per minute */
  getPassiveCreditsPerMin(): number {
    const lv = this.getBuildingLevel('credit_generator');
    return [0, 2, 5, 10][lv] ?? 0;
  }

  /** Forge level (0 = not built, 1-3) */
  getForgeLevel(): number {
    return this.getBuildingLevel('forge');
  }

  /** Collect accumulated passive credits. Call when entering base. */
  collectPassiveCredits(): number {
    const rate = this.getPassiveCreditsPerMin();
    if (rate <= 0) {
      this._lastCreditTick = Date.now();
      return 0;
    }
    const now = Date.now();
    const elapsedMin = (now - this._lastCreditTick) / 60_000;
    this._lastCreditTick = now;
    // Cap at 8 hours to prevent huge accumulation from leaving game idle
    const cappedMin = Math.min(elapsedMin, 480);
    return Math.floor(rate * cappedMin);
  }

  // ─── Helpers ────────────────────────────────────────────

  private footprintsOverlap(
    ax: number, ay: number, aw: number, ah: number,
    bx: number, by: number, bw: number, bh: number,
  ): boolean {
    return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
  }

  /** Check which tiles are occupied by buildings. Returns a Set of "lx,ly" keys. */
  getOccupiedTiles(): Set<string> {
    const occupied = new Set<string>();
    for (const b of this._buildings) {
      const def = getBuildingById(b.id);
      if (!def) continue;
      for (let dy = 0; dy < def.footprintH; dy++) {
        for (let dx = 0; dx < def.footprintW; dx++) {
          occupied.add(`${b.gridX + dx},${b.gridY + dy}`);
        }
      }
    }
    return occupied;
  }

  // ─── Serialization ──────────────────────────────────────

  serialize(): { buildings: PlacedBuilding[]; lastCreditTick: number } {
    return {
      buildings: this._buildings.map(b => ({ ...b })),
      lastCreditTick: this._lastCreditTick,
    };
  }

  deserialize(data: { buildings?: PlacedBuilding[]; lastCreditTick?: number }) {
    if (Array.isArray(data.buildings)) {
      this._buildings = data.buildings
        .filter(b => getBuildingById(b.id)) // validate ids
        .map(b => ({
          id: b.id,
          level: Math.max(1, b.level ?? 1),
          gridX: b.gridX ?? 0,
          gridY: b.gridY ?? 0,
        }));
    }
    if (typeof data.lastCreditTick === 'number') {
      this._lastCreditTick = data.lastCreditTick;
    }
    this.syncBuildingCollision();
  }
}
